import { useMutation } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '../../../components/inputs/LoadingButton';
import Form from '../../../features/admin/approvalSequences/form';
import {
  postApprovalSequence,
  PostApprovalSequenceArgs,
} from '../../../services/admin/ApprovalSequences';
import SuccessFormMessage from '../../../components/messages/SuccessFormMessage';
import { ApprovalSequenceFormFields } from '../../../Interfaces/admin/ApprovalSequence.interface';
import CopyASButton from '../../../features/admin/approvalSequences/CopyASButton';

const ASCreate = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const formMethods = useForm<ApprovalSequenceFormFields>({
    defaultValues: { sequence: [{ step: 1, members: [] }] },
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
  });
  const { handleSubmit } = formMethods;

  const { mutate, isLoading, isSuccess } = useMutation({
    mutationFn: (data: PostApprovalSequenceArgs) => {
      return postApprovalSequence(data);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        'Create Approval Sequence Error',
        errorData?.message || 'An error occurred! please try again later',
        3000
      );
    },
  });

  const onSubmit = (data: ApprovalSequenceFormFields) => {
    const members = data.sequence.reduce((acc, step) => {
      step.members.forEach((member) => {
        const accessID = member.assigneeId
          ? member.id.replace(`--${member.assigneeId}`, '')
          : member.id;
        acc.push({
          step: step.step,
          access: accessID,
          assigneeId: member.assigneeId,
        });
      });
      return acc;
    }, [] as PostApprovalSequenceArgs['members']);
    const payload: PostApprovalSequenceArgs = {
      companyID: data.company,
      report: data.report,
      members: members,
    };

    mutate(payload);
  };

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-8">
      <ErrorToast toastRef={toast} />
      <FormProvider {...formMethods}>
        <div className="text-35px font-bold text-center mb-5 relative">
          <div>Create Approval Sequence</div>
          {!isSuccess && (
            <div className="flex justify-content-center lg:absolute top-0 left-0 mt-4 lg:mt-0 lg:ml-8">
              <CopyASButton />
            </div>
          )}
        </div>
        {!isSuccess && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="max-w-600px w-full mx-auto flex flex-column gap-5"
          >
            <Form />
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                onClick={() => {
                  navigate('/admin/approval-sequences');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
              <LoadingButton
                isLoading={isLoading}
                label="Create"
                className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                fontSize="text-22px"
              />
            </div>
          </form>
        )}
        {isSuccess && (
          <SuccessFormMessage
            message="The approval sequence has been created!"
            redirectUrl="/admin/approval-sequences"
            redirectMessage="Click here to return to the approval sequences page"
          />
        )}
      </FormProvider>
    </div>
  );
};

export default ASCreate;
